import { useCallback, useEffect, useState, type Dispatch, type RefObject, type SetStateAction } from 'react';

// Hard floor/ceiling — tier-clamp läggs ovanpå dessa. Fas 2a: tier-min
// är aldrig under 0.25 (Enterprise) så hard-floor 0.01 är bara skydd
// mot extrem-bugs. Hard-ceiling 4.0 är reserverat för admin-tooling.
export const MIN_CONTENT_SCALE = 0.01;
export const MAX_CONTENT_SCALE = 4;
export const DEFAULT_CONTENT_SCALE = 1;

function getScaleStep(currentScale: number): number {
  if (currentScale <= 0.05) return 0.01;
  if (currentScale <= 0.1) return 0.02;
  if (currentScale <= 0.25) return 0.05;
  if (currentScale <= 0.5) return 0.1;
  return 0.1;
}

function clampToBounds(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

export interface UseCanvasScaleOptions {
  /** Tier-min (0..1). Default: hard-floor 0.01. */
  min?: number;
  /** Tier-max (0..1). Default: hard-ceiling 4.0. */
  max?: number;
  /** False = wheel-listener stängs av + handlers no-op:ar (Free utan zoom). */
  enabled?: boolean;
}

export interface UseCanvasScaleResult {
  contentScale: number;
  setContentScale: Dispatch<SetStateAction<number>>;
  handleScaleUp: () => void;
  handleScaleDown: () => void;
  handleResetScale: () => void;
  /** Den effektiva clamp-funktionen (tier-aware). Exponeras för fit-to-screen. */
  clampScale: (v: number) => number;
}

/**
 * Manages canvas zoom state + Ctrl/meta-wheel zoom listener.
 * Exposes setContentScale so pinch-zoom (touch gestures) can update the same state.
 *
 * Fas 2b: tier-clamp via `min`/`max`-options. `enabled=false` no-op:ar
 * alla zoom-handlers + wheel-listener (för t.ex. zoom-disabled-mobile-
 * fallback om vi någonsin behöver det — i nuvarande spec är alla tiers
 * zoom-enabled så defaulten enabled=true är vanligast).
 */
export function useCanvasScale(
  canvasRef: RefObject<HTMLDivElement>,
  options: UseCanvasScaleOptions = {},
): UseCanvasScaleResult {
  const min = options.min ?? MIN_CONTENT_SCALE;
  const max = options.max ?? MAX_CONTENT_SCALE;
  const enabled = options.enabled !== false;
  const [contentScale, setContentScale] = useState(DEFAULT_CONTENT_SCALE);

  const clampScale = useCallback((v: number) => clampToBounds(v, min, max), [min, max]);

  // Re-clamp:a aktuellt scale om tier-range ändras (t.ex. user upgrade:ar
  // tier eller workspace-ägaren ändras). Annars kan ett gammalt scale-värde
  // ligga utanför nya range:n och bryta UI:s "100%-fast"-knapp m.m.
  useEffect(() => {
    setContentScale(prev => clampToBounds(prev, min, max));
  }, [min, max]);

  const handleScaleUp = useCallback(() => {
    if (!enabled) return;
    setContentScale(prev => clampToBounds(prev + getScaleStep(prev), min, max));
  }, [enabled, min, max]);

  const handleScaleDown = useCallback(() => {
    if (!enabled) return;
    setContentScale(prev => clampToBounds(prev - getScaleStep(prev), min, max));
  }, [enabled, min, max]);

  const handleResetScale = useCallback(() => {
    setContentScale(clampToBounds(DEFAULT_CONTENT_SCALE, min, max));
  }, [min, max]);

  useEffect(() => {
    if (!enabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        setContentScale(prev => clampToBounds(prev * (e.deltaY > 0 ? 0.9 : 1.1), min, max));
      }
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [canvasRef, enabled, min, max]);

  return { contentScale, setContentScale, handleScaleUp, handleScaleDown, handleResetScale, clampScale };
}
